import { Coordinate } from '../model/coordinate';
import Flatten, { Line, Segment, Utils, Vector } from '@flatten-js/core';

// Debounce timeout to share the same timeout object for several debounce calls
let debounceTimeout;

/**
 * Execute callback after specified amount of miliseconds.
 * Execute only the last callback if the previous one has not run yet.
 *
 * @param  {function} callback Callback to be executed
 * @param  {number} wait Number of miliseconds of waiting before executing callback
 */
export function debounce(callback, wait) {
  return (...args) => {
    const context = this;
    clearTimeout(debounceTimeout);
    debounceTimeout = setTimeout(() => callback.apply(context, args), wait);
  };
}

export function getCenter(startPoint, endPoint) {
  return new Coordinate((startPoint.x + endPoint.x) / 2, (startPoint.y + endPoint.y) / 2);
}

export function arePointsClockwise(points) {
  let area = 0;
  for (let i = 0; i < points.length; i++) {
    const j = (i + 1) % points.length;
    area += points[i].x * points[j].y;
    area -= points[j].x * points[i].y;
  }
  return area / 2 > 0;
}

/**
 * Returns the two points defining the line segment of the line crossing the planarea polygon. This line is in the direction of specified angle
 *
 * @param  {Flatten.Point} point
 * @param  {Planarea} planarea
 * @param  {number} angle
 * @return {Array.<Segment>}
 */
export function getSplitLine(point, planarea, angle) {
  const polygon = planarea.getFlattenPolygonOfOriginal();
  const planareaBorders = planarea.getBorders();

  if (!point.on(polygon)) {
    return;
  }

  const lineVector = getPlanareaSplitVector(planarea, angle);

  const line = new Line(point, lineVector);
  const filteredPoints = removeDuplicatePoints(line.intersect(planarea.getFlattenPolygonOfOriginal()));
  const sortedPoints = sortPointsOnLine(filteredPoints);
  const finalPoints = [];

  for (const [index, pt] of sortedPoints.entries()) {
    if (point.equalTo(pt)) {
      // if the tested point lies on the polygon border
      finalPoints.push(pt);
      if (index === 0) {
        const nextSegment = new Segment(sortedPoints[index + 1], pt);
        if (!isSegmentOnAnyBorder(nextSegment, planareaBorders)) {
          finalPoints.push(sortedPoints[index + 1]);
        } else {
          return false;
        }
      } else if (index === sortedPoints.length - 1) {
        const prevSegment = new Segment(sortedPoints[index - 1], pt);
        if (!isSegmentOnAnyBorder(prevSegment, planareaBorders)) {
          finalPoints.push(sortedPoints[index - 1]);
        } else {
          return false;
        }
      } else {
        // create segments from current point to previous and next point
        const previousSegment = new Segment(sortedPoints[index - 1], pt);
        const nextSegment = new Segment(sortedPoints[index + 1], pt);
        if (polygon.contains(previousSegment) && polygon.contains(nextSegment)) {
          // if both segments lie within the polygon, one has to be border, so take the other one
          if (isSegmentOnAnyBorder(previousSegment, planareaBorders)) {
            finalPoints.push(sortedPoints[index + 1]);
          } else {
            finalPoints.push(sortedPoints[index - 1]);
          }
        } else if (polygon.contains(previousSegment) && !isSegmentOnAnyBorder(previousSegment, planareaBorders)) {
          // only previous segment lies in polygon and is not a border
          finalPoints.push(sortedPoints[index - 1]);
        } else if (polygon.contains(nextSegment) && !isSegmentOnAnyBorder(nextSegment, planareaBorders)) {
          // only next segment lies in polygon and is not a border
          finalPoints.push(sortedPoints[index + 1]);
        }
      }
      break;
    } else if (point.lessThan(pt)) {
      // if the point doesnt lie on the border
      if (sortedPoints[index - 1] !== undefined) {
        finalPoints.push(sortedPoints[index - 1]);
        finalPoints.push(pt);
        break;
      }
    }
  }

  return finalPoints.length === 2 ? new Segment(finalPoints[0], finalPoints[1]) : false;
}

/**
 * Remove duplicated points.
 *
 * @param  {Flatten.Point[]} points
 * @returns  {Flatten.Point[]}
 */
export function removeDuplicatePoints(points) {
  const uniquePoints = [];

  points.forEach(point => {
    for (let i = 0; i < uniquePoints.length; i++) {
      if (uniquePoints[i].equalTo(point)) {
        return;
      }
    }
    uniquePoints.push(point);
  });
  return uniquePoints;
}

/**
 * Sort points according to their y and x coordinates respectively
 *
 * @param  {Flatten.Point[]} points
 * @returns {Flatten.Point[]}
 */
export function sortPointsOnLine(points) {
  const sortedPoints = dojo.clone(points);
  sortedPoints.sort((pointA, pointB) => {
    if (pointA.equalTo(pointB)) {
      return 0;
    } else if (pointA.lessThan(pointB)) {
      return -1;
    } else {
      return 1;
    }
  });

  return sortedPoints;
}

/**
 * Returns Flatten.Vector of the split line
 *
 * @param  {Planarea} planarea
 * @param  {number} angle
 */
export function getPlanareaSplitVector(planarea, angle) {
  const baseBorderSegment = planarea.getBaseBorder().getFlattenSegment();
  return new Vector(baseBorderSegment.ps, baseBorderSegment.pe).rotate(
    -0.5 * Math.PI + -1 * planarea.getBoardsAngle() + angle
  );
}

/**
 * Returns all the Borders perpendicular to the provided vector
 *
 * @param  {Flatten.Vector} vector
 * @param  {Planarea} planarea
 * @return {Array<Border>}
 */
export function getAllPerpendicularBorders(vector, planarea) {
  return getAllBordersInAngles([0.5 * Math.PI, 1.5 * Math.PI], vector, planarea);
}

/**
 * Returns all the Borders parallel to the provided vector
 *
 * @param  {Flatten.Vector} vector
 * @param  {Planarea} planarea
 * @return {Array<Border>}
 */
export function getAllParallelBorders(vector, planarea) {
  return getAllBordersInAngles([0, 1 * Math.PI], vector, planarea);
}

/**
 * Returns all borders of the planarea that are in one of the specified angles to the vector
 *
 * @param  {Array<number>} angles
 * @param  {Flatten.Vector} vector
 * @param  {Planarea} planarea
 * @return {Array<Border>}
 */
function getAllBordersInAngles(angles, vector, planarea) {
  const bordersToReturn = [];

  const borders = planarea.getBorders();
  borders.forEach(border => {
    const segment = border.getFlattenSegment();
    const borderVector = segment
      .tangentInEnd()
      .rotate(0.5 * Math.PI)
      .normalize();
    for (const angle of angles) {
      if (isEqualInTolerance(vector.angleTo(borderVector), angle)) {
        bordersToReturn.push(border);
        break;
      }
    }
  });

  return bordersToReturn;
}

/**
 * Returns true if the two numbers are equal up to the Flatten-JS precision constant
 *
 * @param  {number} number1
 * @param  {number} number2
 */
export function isEqualInTolerance(number1, number2, tolerance = Utils.getTolerance()) {
  return Math.abs(number1 - number2) < tolerance;
}
/**
 * Checks if given Segment is on any planarea Borders
 *
 * @param  {Flatten.Segment} segment
 * @param  {Array<Border>} borders
 */
function isSegmentOnAnyBorder(segment, borders) {
  for (const border of borders) {
    const borderSegment = border.getFlattenSegment();
    if (borderSegment.contains(segment.ps) && borderSegment.contains(segment.pe)) {
      return true;
    }
  }

  return false;
}

export function getPolygonPoints(polygon) {
  const polyPoints = [];
  for (const edge of [...polygon.faces][0]) {
    polyPoints.push(edge.shape.ps);
  }
  return polyPoints;
}

/**
 * Get points of Flatten Face
 * @param  {Flatten.Face} face
 * @returns {Array.<Flatten.Point>}
 */
export function getFacePoints(face) {
  const polyPoints = [];
  for (const edge of face) {
    polyPoints.push(edge.shape.ps);
  }
  return polyPoints;
}

/**
 * Order newPoints in the same order as the original points are ordered.
 * We assume, that there is at least one similar point and the points in the array describe polygon in the same CCW/CW order.
 *
 * @param  {Array.<Flatten.Point>} originalPoints
 * @param  {Array.<Flatten.Point>} newPoints
 * @returns {Array.<Flatten.Point>}
 */
export function orderPoints(pointsToMatch, pointsToOrder) {
  const originalPoints = dojo.clone(pointsToMatch);
  const newPoints = dojo.clone(pointsToOrder);
  // Order the new points in the same order as the original ones are
  let indexOfMatchOld = -1;
  let indexOfMatchNew = -1;
  // find indexes of first matching elements
  for (let i = 0; i < originalPoints.length; i++) {
    const point = originalPoints[i];
    for (let j = 0; j < newPoints.length; j++) {
      const newPoint = newPoints[j];
      if (point.equalTo(newPoint)) {
        indexOfMatchOld = i;
        indexOfMatchNew = j;
        break;
      }
    }
    if (indexOfMatchNew > -1) {
      break;
    }
  }
  const arrayRotateCount =
    indexOfMatchNew >= indexOfMatchOld
      ? indexOfMatchNew - indexOfMatchOld
      : indexOfMatchNew + newPoints.length - indexOfMatchOld;
  // shift the newAreaPoints to match the order
  for (let i = 0; i < arrayRotateCount; i++) {
    const firstElement = newPoints.shift();
    newPoints.push(firstElement);
  }
  return newPoints;
}

export function degreesToRadians(degrees) {
  return (degrees * Math.PI) / 180;
}

export function radiansToDegrees(radians) {
  return (radians * 180) / Math.PI;
}

export function degreesToPercent(angle) {
  return Math.tan(degreesToRadians(angle)) * 100;
}

export function percentToDegrees(percent) {
  return radiansToDegrees(Math.atan(percent / 100));
}

export function containsPoint(array, point) {
  return array.some(elem => {
    return elem.equalTo(point);
  });
}

export function getLengthCountsForArea(rawPlanStructure) {
  let boards = rawPlanStructure.freeboards.full.concat(rawPlanStructure.freeboards.waste);
  boards = boards.concat(rawPlanStructure.fullboards);

  const boardCounts = {};
  for (const board of boards) {
    const boardLength = board['length'];
    if (boardLength in boardCounts) {
      boardCounts[boardLength]++;
    } else {
      boardCounts[boardLength] = 1;
    }
  }
  return boardCounts;
}

export function mergeTwoLengthCounts(boardCountsA, boardCountsB) {
  const boardCounts = { ...boardCountsA };

  for (const length in boardCountsB) {
    if (length in boardCounts) {
      boardCounts[length] += boardCountsB[length];
    } else {
      boardCounts[length] = boardCountsB[length];
    }
  }

  return boardCounts;
}
